<template>
  <div class="searchTemplate">
    <div class="searchTemplate__wrapper">
      <breadcrumbs
        v-if="breadcrumbs"
        :list="breadcrumbs"
        type="search"
        :search-page="searchPage"
        class="searchTemplate__breadcrumbs"
      />
      <breadcrumbs
        v-else
        :list="[
          {
            url: '/',
            title: 'Ofix',
          },
          {
            url: '/',
            title: currentBreadcrumb,
          },
        ]"
        class="searchTemplate__breadcrumbs"
      />
      <div
        v-if="categoryFilter && categoryFilter.id"
        ref="mobileBreadcrumbs"
        class="searchTemplate__mobileBreadcrumbs"
      >
        <mobile-breadcrumbs
          v-if="searchPage"
          :title="categoryFilter.title"
          :to="localePath({ name: 'category-slug', params: { slug: categoryFilter.slug } })"
        />
        <mobile-breadcrumbs
          v-else
          :title="categoryFilter.title"
          @click="selectCategory([categoryFilter.id])"
        />
      </div>
      <div v-else ref="mobileBreadcrumbs" class="searchTemplate__mobileBreadcrumbs">
        <mobile-breadcrumbs title="Wszystkie kategorie" :to="localePath({ name: 'search' })" />
      </div>

      <div class="searchTemplate__title">
        <component
          :is="showHeading ? 'h1' : 'div'"
          v-if="categoryName"
          class="searchTemplate__titleText"
          >{{ categoryName }}
        </component>
        <component :is="showHeading ? 'h1' : 'div'" v-if="query" class="searchTemplate__titleText">
          Szukasz "{{ query }}"
        </component>
        <component
          :is="showHeading ? 'h1' : 'div'"
          class="searchTemplate__titleText"
          v-if="searchTitle && !categoryName"
        >
          {{ searchTitle }}
        </component>
        <span v-if="pagination.countTotal" class="searchTemplate__titleNumber">
          ({{ pagination.countTotal }} produktów)
        </span>
      </div>
      <div class="searchTemplate__inner">
        <div class="searchTemplate__sidebar">
          <div class="searchTemplate__categories">
            <search-categories :search-page="searchPage" />
          </div>
          <div class="searchTemplate__filters">
            <div class="searchTemplate__filtersTitle">
              {{ $t('filters') }}
            </div>
            <div class="searchTemplate__filtersContent">
              <search-filters prefix="desktop" />
            </div>
          </div>
        </div>
        <div class="searchTemplate__content">
          <slot name="infoBox"></slot>
          <slot name="searchContent"></slot>
          <div
            class="searchTemplate__contentToners"
            v-if="$route.query.inkAndTonersOnly && $route.query.inkAndTonersOnly === 'true'"
          >
            <search-toners-inks is-search-page title="Wyszukaj" />
          </div>
          <div
            v-if="fuzzySearch"
            class="searchTemplate__empty searchTemplate__empty--disableMarginTop"
          >
            <info-bar
              :with-close="false"
              :message="`Nie znaleziono dokładnych wyników dla: <b>${fuzzySearch}</b> Wyświetlamy podobne wyniki.`"
              icon="warning"
              color="#ff6e00"
            />
          </div>
          <div
            class="searchTemplate__seoBoxTop"
            v-if="seoDescriptionUp && seoDescriptionUp.blocks && seoDescriptionUp.blocks.length"
          >
            <EditorSearchSeoSections :blocks="seoDescriptionUp.blocks" />
          </div>
          <div class="searchTemplate__result">
            <div class="searchTemplate__resultHeader">
              <div v-if="promotionHeaderContent" class="searchTemplate__resultHeaderPromotion">
                <img
                  v-if="promotionHeaderContent.image"
                  :src="promotionHeaderContent.image"
                  alt=""
                  class="searchTemplate__resultImg searchTemplate__resultHeaderPromotionImage"
                />
                <div
                  v-if="promotionHeaderContent.description"
                  class="searchTemplate__resultHeaderPromotionDesc"
                >
                  <cms-wysiwyg :html="promotionHeaderContent.description" />
                </div>
              </div>
              <active-filters :test-props="activeFilters" class="searchTemplate__resultFilters" />
            </div>
            <filters-bar
              :sticky-breakpoint="768"
              :show-grid-type="!!items && !hasCatalogSubset"
              type="search"
              class="searchTemplate__settings"
              show-mobile-filters
              show-mobile-categories
              :use-pagination-href="true"
              :route-name="routeName"
              :route-pagination-name="routePaginationName"
              @grid-change="gridChange"
            />
            <div v-if="!items" class="searchTemplate__empty">
              <info-bar
                :with-close="false"
                message="Przepraszamy, nie znaleźliśmy wyników."
                icon="warning"
                color="#ff6e00"
              />

              <div v-if="hasCatalogSubset" class="searchTemplate__catalogSubset">
                <catalog-subset-banner is-border />
              </div>

              <search-contact-form v-if="!hasCatalogSubset" :search-phrase="query" />
            </div>

            <template v-if="items && items.length">
              <template v-if="hasCatalogSubset">
                <CatalogSubsetProductsList :products="items" />
              </template>
              <template v-else>
                <product-grid
                  v-if="gridType === 'grid' && !$device.isMobile"
                  :products="items"
                  class="searchTemplate__grid"
                  :list-name="listName"
                />
                <product-list
                  v-if="gridType === 'list' || $device.isMobile"
                  :products="items"
                  class="searchTemplate__list"
                  :list-name="listName"
                />
              </template>

              <pagination
                :pagination="pagination"
                light-version
                center
                use-href
                :route-name="routeName"
                :route-pagination-name="routePaginationName"
                class="searchTemplate__resultPagination"
                list-with-newsletter
                @change="changePage"
              />
            </template>
          </div>
          <div class="searchTemplate__seo" v-if="seoInfo && seoInfo !== '<p></p>'">
            <seo-box :content="seoInfo" class="searchTemplate__seo" />
          </div>
          <div
            class="searchTemplate__seoBoxBottom"
            v-if="
              seoDescriptionDown && seoDescriptionDown.blocks && seoDescriptionDown.blocks.length
            "
          >
            <EditorSearchSeoSections :blocks="seoDescriptionDown.blocks" />
          </div>
          <div
            class="searchTemplate__seo"
            v-if="jsonDescription && jsonDescription.blocks && jsonDescription.blocks.length"
          >
            <EditorSearchSeoSections :blocks="jsonDescription.blocks" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import slugify from 'slugify';
import { mapActions, mapMutations, mapState } from 'vuex';
import Sticky from 'sticky-js';
import searchMixin from '~/mixins/search';
import productsFiltersMixin from '~/mixins/productsFilters';
import Breadcrumbs from '~/components/Breadcrumbs';
import ActiveFilters from '~/components/Search/SearchActiveFilters';
import ProductGrid from '~/components/Product/ProductGrid';
import ProductList from '~/components/Product/ProductList';
import SearchCategories from '~/components/Search/SearchCategories';
import MobileBreadcrumbs from '~/components/MobileBreadcrumbs';
import SeoBox from '~/components/SeoBox';
import VButton from '~/components/VButton';
import InfoBar from '~/components/InfoBar';
import CmsWysiwyg from '~/components/Cms/CmsWysiwyg';
import ContentsSection from '~/components/ContentsSection';
import ContentsKey from '~/components/ContentsKey';
import SearchTonersInks from '~/containers/Search/SearchTonersInks';
import EditorSearchSeoSections from '@/components/Editor/SearchSeo/EditorSearchSeoSections';
import FiltersBar from '~/components/FiltersBar';
import SearchContactForm from '@/components/Search/SearchContactForm';
import CatalogSubsetBanner from '@/components/CatalogSubset/CatalogSubsetBanner.vue';
import CatalogSubsetProductsList from '@/components/CatalogSubset/CatalogSubsetProductsList.vue';

export default {
  name: 'SearchTemplate',
  components: {
    CatalogSubsetProductsList,
    CatalogSubsetBanner,
    SearchContactForm,
    FiltersBar,
    EditorSearchSeoSections,
    SearchTonersInks,
    ContentsKey,
    ContentsSection,
    CmsWysiwyg,
    SeoBox,
    MobileBreadcrumbs,
    SearchCategories,
    ProductList,
    ProductGrid,
    ActiveFilters,
    Breadcrumbs,
    VButton,
    InfoBar,
  },
  mixins: [searchMixin('search'), productsFiltersMixin('search')],
  props: {
    defaultCategoryId: {
      type: [String, Number],
      default: undefined,
    },
    promotionGroupId: {
      type: String,
      default: undefined,
    },
    onlyPalletPacks: {
      type: Boolean,
      default: false,
    },
    onlyEco: {
      type: Boolean,
      default: false,
    },
    searchTitle: {
      type: String,
      default: undefined,
    },
    promotionHeaderContent: {
      type: Object,
      default: () => {},
    },
    brandId: {
      type: Number,
      default: null,
    },
    catalogSubsetId: {
      type: Number,
      default: null,
    },
    atsQuery: {
      type: String,
      default: null,
    },
    currentBreadcrumb: {
      type: String,
      default: 'Wyszukiwarka',
    },
    searchPage: {
      type: Boolean,
      default: false,
    },
    listName: {
      type: String,
      default: 'Search list',
    },
    routeName: {
      type: String,
      default: 'search',
    },
    routePaginationName: {
      type: String,
      default: 'search-page-number',
    },
    seoInfo: {
      type: String,
      default: '',
    },
    jsonDescription: {
      type: Object,
      default: () => {},
    },
    seoDescriptionDown: {
      type: Array,
      default: () => [],
    },
    seoDescriptionUp: {
      type: Array,
      default: () => [],
    },
    showHeading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      unsubscribe: null,
      searchParams: {},
      gridType: this.$cookies.get('grid-style-search')
        ? this.$cookies.get('grid-style-search')
        : 'grid',
      stickyBreadcrumbs: null,
      openMobileCategories: false,
    };
  },
  async fetch() {
    try {
      this.getFiltersFromUrl(this.activeFilters, false, this.$route.fullPath);
      if (this.defaultCategoryId) {
        this.setCategories({ type: 'search', value: [this.defaultCategoryId] });
      }
      if (this.promotionGroupId) {
        this.setPromotionGroupId({ type: 'search', value: this.promotionGroupId });
      }
      if (this.brandId) {
        this.setBrandId({ type: 'search', value: this.brandId });
      }
      if (this.catalogSubsetId) {
        this.setCatalogSubsetId({ type: 'search', value: this.catalogSubsetId });
      }
      if (this.atsQuery) {
        this.setAtsQuery({ type: 'search', value: this.atsQuery });
      }
      this.setOnlyPalletPacks({ type: 'search', value: this.onlyPalletPacks });
      this.setOnlyEco({ type: 'search', value: this.onlyEco });

      await this.fetchData({ type: 'search' });
    } catch {}
  },
  computed: {
    ...mapState({
      activeFilters: state => state.productsFilters.search.activeFilters,
      breadcrumbs: state => state.productsFilters.search.breadcrumbs,
      categoryName: state => state.productsFilters.search.categoryName,
      categoryFilter: state => state.productsFilters.search.categoryFilter,
      categories: state => state.productsFilters.search.categories,
      query: state => state.productsFilters.search.query,
      fuzzySearch: state => state.productsFilters.search.fuzzySearch,
      popstate: state => state.productsFilters.search.popstate,
      device: state => state.productsFilters.search.device,
      deviceBrand: state => state.productsFilters.search.deviceBrand,
      deviceModel: state => state.productsFilters.search.deviceModel,
      oemCode: state => state.productsFilters.search.oemCode,
      inkAndTonersOnly: state => state.productsFilters.search.inkAndTonersOnly,
    }),
  },
  watch: {
    '$fetchState.pending'(val) {
      if (!val) {
        this.gTagProductImpressions(this.items);
      }
    },
  },
  mounted() {
    window.addEventListener('popstate', this.popStateWatch);
    this.setHeaderPrefix('searchTemplate');
    this.scrollDownMobileHide(true);
    this.initSticky();
    this.unsubscribe = this.$store.subscribeAction((action, state) => {
      if (
        action.type === 'productsFilters/fetchData' &&
        action.payload.type === 'search' &&
        !this.popstate
      ) {
        this.updateSearchUrl();
      }
    });
    if (!this.$fetchState.pending) {
      this.gTagProductImpressions(this.items);
    }
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  destroyed() {
    window.removeEventListener('popstate', this.popStateWatch);
    this.setHeaderPrefix('');
    this.scrollDownMobileHide(false);
    if (this.stickyBreadcrumbs) {
      this.stickyBreadcrumbs.destroy();
    }
    this.resetAutocomplete();
  },
  methods: {
    ...mapMutations({
      setHeaderPrefix: 'layout/setHeaderPrefix',
      scrollDownMobileHide: 'layout/scrollDownMobileHide',
      setPopstate: 'productsFilters/setPopstate',
      resetAutocomplete: 'autocomplete/resetAutocomplete',
      setPromotionGroupId: 'productsFilters/setPromotionGroupId',
      setBrandId: 'productsFilters/setBrandId',
      setCatalogSubsetId: 'productsFilters/setCatalogSubsetId',
      setAtsQuery: 'productsFilters/setAtsQuery',
      setOnlyPalletPacks: 'productsFilters/setOnlyPalletPacks',
      setOnlyEco: 'productsFilters/setOnlyEco',
      setQuery: 'productsFilters/setQuery',
    }),
    ...mapActions({
      changeCategory: 'productsFilters/changeCategory',
    }),
    popStateWatch() {
      if (this.$route.name.split('___')[0] === this.routeName) {
        this.setPopstate({ type: 'search', value: true });
        this.getFiltersFromUrl(this.activeFilters, false);
        if (this.defaultCategoryId) {
          this.setCategories({ type: 'search', value: [this.defaultCategoryId] });
        }
        this.fetchData({ type: 'search' });
      }
    },
    updateSearchUrl() {
      this.updateUrlParams(this.localePath({ name: this.routeName }), {
        ...this.activeFilters,
        ...(!this.defaultCategoryId && { categories: this.categories }),
        ...{ query: this.query },
        ...{ orderBy: this.sort },
        ...{ limit: this.pagination.limit },
        ...{ device: this.device },
        ...{ deviceModel: this.deviceModel },
        ...{ deviceBrand: this.deviceBrand },
        ...{ oemCode: this.oemCode },
        ...{ inkAndTonersOnly: this.inkAndTonersOnly },
      });
    },
    initSticky() {
      if (this.stickyBreadcrumbs) {
        this.stickyBreadcrumbs.destroy();
        this.stickyBreadcrumbs = null;
      }
      if (!this.$refs.mobileBreadcrumbs) return;
      this.stickyBreadcrumbs = new Sticky('.searchTemplate__mobileBreadcrumbs', {
        stickyClass: 'searchTemplate__mobileBreadcrumbs--sticky',
        wrap: true,
      });
    },
    gridChange(type) {
      this.gridType = type;
    },
    selectCategory(id) {
      this.changeCategory({ type: 'search', value: id });
    },
    gTagProductImpressions(items) {
      if (!items || !items.length) return;
      const mapItems = this.items.map(
        (
          {
            name,
            id,
            priceObject,
            priceBeforeDiscountObject,
            brand,
            category,
            variant,
            list,
            position,
            promotion,
          },
          index,
        ) => {
          const categories = category.namesInPath.reduce((acc, curr, index) => {
            acc['category' + (index === 0 ? '' : index + 1)] = curr;
            return acc;
          }, {});
          return {
            item_id: id,
            item_name: name,
            index,
            discount: priceBeforeDiscountObject
              ? this.$helper.calculateDiscount(priceBeforeDiscountObject.gross, priceObject.gross)
              : 0,
            item_brand: brand ? brand.name : '',
            item_list_id: slugify(this.listName),
            item_list_name: this.listName,
            ...categories,
            variant: '',
            promotion_id: promotion?.id ?? '',
            promotion_name: promotion?.name ?? '',
            price: priceObject.gross,
            quantity: 1,
          };
        },
      );

      if (this.promotionGroupId) {
        this.$gtm.push({ ecommerce: null });
        this.$gtm.push({
          event: 'view_promotion',
          ecommerce: {
            creative_name: `Strona promocji - ${this.searchTitle}`,
            creative_slot: 'strona-promocji',
            promotion_id: this.promotionGroupId,
            promotion_name: this.searchTitle,
            items: mapItems,
          },
        });
      }

      this.$gtm.push({ ecommerce: null });
      this.$gtm.push({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: slugify(this.listName),
          item_list_name: this.listName,
          items: mapItems,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.searchTemplate {
  $root: &;

  &__wrapper {
    transition: opacity $tr;

    &--loading {
      opacity: 0.5;

      @include rwd('large-tablet') {
        opacity: 1;
      }
    }
  }

  &__breadcrumbs {
    @include rwd('tablet') {
      display: none;
    }
  }

  &__mobileBreadcrumbs {
    position: relative;
    display: none;
    z-index: $zHeader - 1;

    @include rwd('tablet') {
      display: flex;
    }
  }

  &__title {
    @include rwd('tablet') {
      padding-top: 3.2rem;
    }
  }

  &__titleText {
    padding-right: 0.8rem;
    display: inline;
    font-size: 3.6rem;
    line-height: 4.5rem;
    font-weight: 600;

    @include rwd('tablet') {
      padding-top: 3.2rem;
      font-size: 3rem;
      line-height: 3.8rem;
    }
  }

  &__titleNumber {
    font-size: 1.8rem;
    line-height: 2.3rem;
    color: $cAddDark02;
  }

  &__inner {
    display: flex;
    flex-wrap: wrap;
    margin-top: 3rem;
  }

  &__sidebar {
    padding-right: 3.6rem;
    flex: 0 0 350px;
    max-width: 350px;

    @include rwd('large-laptop') {
      padding-right: 3rem;
      flex: 0 0 280px;
      max-width: 280px;
    }
  }

  &__content {
    overflow: hidden;
    flex: 0 0 calc(100% - 350px);
    max-width: calc(100% - 350px);

    @include rwd('large-laptop') {
      flex: 0 0 calc(100% - 280px);
      max-width: calc(100% - 290px);
    }

    @include rwd('large-tablet') {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  &__resultHeader {
    background-color: $cWhite;
    padding: 0 2rem;

    @include rwd('tablet') {
      width: calc(100% + 40px);
      margin-left: -20px;
      // padding-top: 0;
      // padding-bottom: 0;
    }
  }

  &__categories {
    @include rwd('large-tablet') {
      display: none;
    }
  }

  &__resultImg {
    display: flex;
    width: 100%;
    margin-bottom: 3rem;
    padding-top: 2rem;

    @include rwd('tablet') {
      width: calc(100% + 4rem);
      margin-left: -2rem;
      margin-bottom: 2rem;
    }
  }

  &__resultHeaderPromotion {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 2rem 0;

    @include rwd('small-tablet') {
      flex-direction: column;
      align-items: center;
    }
  }

  &__resultHeaderPromotionImage {
    width: auto;
    height: auto;
    object-fit: contain;
    flex: 0 0 25%;
    padding-right: 2rem;
    max-width: 25%;
    padding-top: 0;

    @include rwd('tablet') {
      flex: 0 0 40%;
      max-width: 40%;
      padding-left: 2rem;
    }

    @include rwd('small-tablet') {
      flex: 0 0 100%;
      max-width: 100%;
      margin: 0 auto;
      padding: 0 0 2rem 0;
    }
  }

  &__resultHeaderPromotionDesc {
    flex: 0 0 75%;
    padding-right: 2rem;
    max-width: 75%;
    @include rwd('tablet') {
      flex: 0 0 60%;
      max-width: 60%;
    }

    @include rwd('small-tablet') {
      flex: 0 0 100%;
      max-width: 100%;
      padding-right: 0;
    }
  }

  &__resultContents {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      left: -2rem;
      bottom: 0;
      width: calc(100% + 4rem);
      height: 1px;
      background-color: $cAddLight;
    }
  }

  &__filters {
    background-color: $cWhite;

    @include rwd('large-tablet') {
      display: none;
    }
  }

  &__filtersTitle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2rem;
    font-size: 1.6rem;
    line-height: 2rem;
    font-weight: 600;
    border-bottom: 1px solid $cAddLight;
  }

  &__filtersContent {
    padding: 2rem 2rem 3rem 2rem;
  }

  &__resultFilters {
    @include rwd('tablet') {
      display: none;
    }
  }

  &__settings {
    padding: 3rem;

    @include rwd('ultra') {
      padding: 2rem 3rem;
    }

    @include rwd('large-tablet') {
      padding: 1.5rem 3rem;
    }

    @include rwd('tablet') {
      width: calc(100% + 40px);
      margin-left: -20px;
      border-top: 0;
    }

    @include rwd('small-tablet') {
      padding: 0.8rem 2.3rem;
    }
  }

  &__grid {
    margin-bottom: 1.5rem;
    margin-top: 1.6rem;
  }

  &__list {
    margin-bottom: 3.2rem;
    margin-top: 1.6rem;
    padding: 0 2rem;
    background-color: $cWhite;

    @include rwd('tablet') {
      padding: 0;
    }
  }

  &__seoBoxBottom {
    margin-top: 1.6rem;
  }

  &__seo {
    margin-top: 5rem;
  }

  &__empty {
    background-color: $cWhite;
    padding: 2rem;
    margin: 2rem 0;

    &--disableMarginTop {
      margin: 0 0 2rem;
    }
  }

  &__contentToners {
    background: $cWhite;
    margin-bottom: 1.6rem;
    padding: 2rem;
  }

  &__seoBoxTop {
    margin-bottom: 1.6rem;
  }

  &__catalogSubset {
    margin-top: 2rem;
  }
}
</style>
